// hooks
import usePreviousValue from '@hooks/use-previous-value';
// utils
import {
  contactIconsColorHaveChanged,
  generateContactDetailsIconImage,
} from '@modules/email-signatures-helpers';
import { isEqual } from 'es-toolkit';
// es-toolkit/compat: lodash filter iterates objects/Records (not in es-toolkit main)
import { filter } from 'es-toolkit/compat';
import { useEffect, useMemo } from 'react';

// consts and types
import type {
  EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFieldId,
  EmailSignatureV2,
} from '../types';

/**
 * This hook will detect any changes to the email signature contact details fields and generate new
 * contact details icons accordingly.
 * @param emailSignature The email signature object
 * @param handleUpdateNewLocalContactIconUrl The callback function that is used to update the contact
 * detail icon with the newly generated icon
 * @param enable The flag indicates whether the hook should run
 */
function useContactDetailsIconsGenerator(
  emailSignature: EmailSignatureV2,
  handleUpdateNewLocalContactIconUrl: (
    contactFieldId: EmailSignatureContactDetailsFieldId,
    imageUrl: string,
  ) => void,
  enable: boolean,
): void {
  const previousEmailSignature = usePreviousValue(
    emailSignature,
    (prevEmailSignature, currentEmailSignature) =>
      enable ? isEqual(prevEmailSignature, currentEmailSignature) : true,
  );

  const { contactFieldsIconsColor } = useContactDetailsDesign(emailSignature);

  const contactFieldsThatHaveNewIcons = useMemo(() => {
    if (!enable || !previousEmailSignature) return [];

    const previousEmailSignatureContactDetails = previousEmailSignature.contactDetails;
    // we filter out the contact fields that have new icons that needed to be regenerated,
    // a contact field will have a new icon when its base icon change or the color of contact
    // icons changes
    return filter(
      emailSignature.contactDetails,
      (contactDetailsField) =>
        !isEqual(
          contactDetailsField.baseIcon,
          previousEmailSignatureContactDetails[contactDetailsField.id]?.baseIcon,
        ) || contactIconsColorHaveChanged(previousEmailSignature.design, emailSignature.design),
    );
  }, [emailSignature.contactDetails, emailSignature.design, enable, previousEmailSignature]);

  useEffect(() => {
    // For each contact field that has a new icon, we will generate a new icon, convert
    // them to image and call a callback update function with this new icon image
    contactFieldsThatHaveNewIcons.forEach((contactFieldThatHasNewIcon) => {
      const contactDetailsFieldNewIconImage = generateContactDetailsIconImage(
        contactFieldThatHasNewIcon.baseIcon,
        contactFieldsIconsColor,
      );
      handleUpdateNewLocalContactIconUrl(
        contactFieldThatHasNewIcon.id,
        contactDetailsFieldNewIconImage ?? '',
      );
    });
  }, [contactFieldsIconsColor, contactFieldsThatHaveNewIcons, handleUpdateNewLocalContactIconUrl]);
}

/**
 * This hook is similar to the hook above, but it only generates new icon for the dummy contact details
 * @param emailSignature The email signature object
 * @param handleUpdateDummyContactIconUrl The callback function that is used to update the dummy contact
 * detail icon with the newly generated icon
 * @param standardCompactContactFields The dummy contact details fields
 */
export function useDummyContactDetailsIconsGenerator(
  emailSignature: EmailSignatureV2,
  handleUpdateDummyContactIconUrl: (index: number, iconImageUrl: string) => void,
  standardCompactContactFields: EmailSignatureContactDetailsField[],
): void {
  const { contactFieldsIconsColor } = useContactDetailsDesign(emailSignature);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    standardCompactContactFields.forEach((dummyContactField, index) => {
      const contactDetailsFieldNewIconImage = generateContactDetailsIconImage(
        dummyContactField.baseIcon,
        contactFieldsIconsColor,
      );

      handleUpdateDummyContactIconUrl(index, contactDetailsFieldNewIconImage ?? '');
    });
  }, [
    contactFieldsIconsColor,
    standardCompactContactFields,
    emailSignature.design,
    handleUpdateDummyContactIconUrl,
  ]);
}

/**
 * This hook detects changes to email signature design and calculates the new colour of the contact details icons
 * @param emailSignature The email signature object
 */
function useContactDetailsDesign(emailSignature: EmailSignatureV2): {
  contactFieldsIconsColor: string;
} {
  // calculate the color of the contact fields
  const contactFieldsIconsColor = useMemo(() => {
    if (emailSignature.design.contactDetailsIconUseThemeColor) {
      return emailSignature.design.themeColor;
    } else {
      return emailSignature.design.contactDetailsIconColor || emailSignature.design.themeColor;
    }
  }, [
    emailSignature.design.contactDetailsIconColor,
    emailSignature.design.contactDetailsIconUseThemeColor,
    emailSignature.design.themeColor,
  ]);

  return { contactFieldsIconsColor };
}

export default useContactDetailsIconsGenerator;
